import type {
  WorkspaceConversationCreatedDto,
  WorkspaceConversationDetailDto,
  WorkspaceConversationListDto,
  WorkspaceConversationMessageDto,
  WorkspaceConversationListItemDto,
} from '../../features/opportunities/opportunity-wizard/steps/smart-discovery/models/workspace-conversation.models';
import { MOCK_SMART_DISCOVERY_CHAT_BODY } from './smart-discovery-chat-mock.response';
import { SMART_DISCOVERY_ANSWER_MODEL_CATALOG } from '../../features/opportunities/opportunity-wizard/steps/smart-discovery/models/smart-discovery-answer-model-catalog.fixture';

const MOCK_CONV_ALPHA = 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa';
const MOCK_CONV_BETA = 'bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb';

const baseTime = '2026-04-10T10:00:00.000Z';

/** Mutable list returned by GET …/conversations (seed + dynamically added). */
let mockConversationHeaders: WorkspaceConversationListItemDto[] = [
  {
    conversationId: MOCK_CONV_ALPHA,
    kind: 'SmartDiscovery',
    opportunityId: null,
    title: 'Qualification overview',
    messageCount: 4,
    createdAtUtc: baseTime,
    updatedAtUtc: '2026-04-10T11:30:00.000Z',
  },
  {
    conversationId: MOCK_CONV_BETA,
    kind: 'SmartDiscovery',
    opportunityId: null,
    title: 'Risks and budget',
    messageCount: 2,
    createdAtUtc: '2026-04-09T08:00:00.000Z',
    updatedAtUtc: '2026-04-09T09:15:00.000Z',
  },
];

const mockMessagesByConversationId = new Map<string, WorkspaceConversationMessageDto[]>();

function seedMessages(): void {
  mockMessagesByConversationId.set(MOCK_CONV_ALPHA, [
    {
      sequence: 1,
      messageId: 'sd-mock-a-1',
      role: 'user',
      content: 'What should we validate first?',
      intent: null,
      createdAtUtc: '2026-04-10T10:01:00.000Z',
    },
    {
      sequence: 2,
      messageId: 'sd-mock-a-2',
      role: 'assistant',
      content:
        'Start with **commercial fit** and **delivery capacity** before deep technical design.',
      intent: 'qualify_scope',
      createdAtUtc: '2026-04-10T10:01:05.000Z',
      evidence: [],
    },
    {
      sequence: 3,
      messageId: 'sd-mock-a-3',
      role: 'user',
      content: 'Any red flags on timeline?',
      intent: null,
      createdAtUtc: '2026-04-10T10:05:00.000Z',
      focusedFinding: {
        id: 'finding:mock-timeline',
        area: 'Motivation',
        findingKind: 'Risk',
        severity: 'Amber',
        title: 'Aggressive delivery window',
      },
    },
    {
      sequence: 4,
      messageId: 'sd-mock-a-4',
      role: 'assistant',
      content: 'Watch **procurement freeze windows** and dependency on third-party data feeds.',
      intent: 'timeline_risk',
      createdAtUtc: '2026-04-10T10:05:08.000Z',
      evidence: MOCK_SMART_DISCOVERY_CHAT_BODY.evidence.slice(0, 1),
      followUpSuggestions: [],
    },
  ]);

  mockMessagesByConversationId.set(MOCK_CONV_BETA, [
    {
      sequence: 1,
      messageId: 'sd-mock-b-1',
      role: 'user',
      content: 'Summarise budget sensitivity.',
      intent: null,
      createdAtUtc: '2026-04-09T08:05:00.000Z',
    },
    {
      sequence: 2,
      messageId: 'sd-mock-b-2',
      role: 'assistant',
      content: 'Assume **fixed cap** unless client signals flexibility on scope trade-offs.',
      intent: 'commercial',
      createdAtUtc: '2026-04-09T08:05:12.000Z',
      evidence: [],
      followUpSuggestions: ['What does this imply for the bid?'],
    },
  ]);
}

seedMessages();

function findHeader(conversationId: string): WorkspaceConversationListItemDto | undefined {
  return mockConversationHeaders.find((header) => header.conversationId === conversationId);
}

function upsertHeaderForNewConversation(conversationId: string): void {
  const existing = findHeader(conversationId);
  if (existing) {
    return;
  }
  const now = new Date().toISOString();
  mockConversationHeaders = [
    {
      conversationId,
      kind: 'SmartDiscovery',
      opportunityId: null,
      title: null,
      messageCount: 0,
      createdAtUtc: now,
      updatedAtUtc: now,
    },
    ...mockConversationHeaders,
  ];
}

function bumpMessageCount(conversationId: string, delta: number): void {
  mockConversationHeaders = mockConversationHeaders.map((header) =>
    header.conversationId === conversationId
      ? {
          ...header,
          messageCount: header.messageCount + delta,
          updatedAtUtc: new Date().toISOString(),
        }
      : header,
  );
}

function appendMockChatTurn(conversationId: string, userMessage: string): void {
  upsertHeaderForNewConversation(conversationId);
  const rows = mockMessagesByConversationId.get(conversationId) ?? [];
  const nextSequence = rows.length === 0 ? 1 : Math.max(...rows.map((row) => row.sequence)) + 1;
  const now = new Date().toISOString();
  const userRow: WorkspaceConversationMessageDto = {
    sequence: nextSequence,
    messageId: `sd-mock-u-${nextSequence}`,
    role: 'user',
    content: userMessage,
    intent: null,
    createdAtUtc: now,
  };
  const assistantRow: WorkspaceConversationMessageDto = {
    sequence: nextSequence + 1,
    messageId: `sd-mock-as-${nextSequence}`,
    role: 'assistant',
    content: MOCK_SMART_DISCOVERY_CHAT_BODY.answer,
    intent: MOCK_SMART_DISCOVERY_CHAT_BODY.intent,
    createdAtUtc: now,
    evidence: MOCK_SMART_DISCOVERY_CHAT_BODY.evidence,
    followUpSuggestions: MOCK_SMART_DISCOVERY_CHAT_BODY.followUpSuggestions,
    ...(MOCK_SMART_DISCOVERY_CHAT_BODY.items?.length
      ? { items: MOCK_SMART_DISCOVERY_CHAT_BODY.items }
      : {}),
  };
  mockMessagesByConversationId.set(conversationId, [...rows, userRow, assistantRow]);
  bumpMessageCount(conversationId, 2);
}

export function mockHandleWorkspaceConversationsListGet(): {
  status: number;
  body: WorkspaceConversationListDto;
} {
  return {
    status: 200,
    body: {
      items: mockConversationHeaders.map((item) => ({ ...item })),
      continuationToken: null,
    },
  };
}

export function mockHandleWorkspaceConversationsCreatePost(body: unknown): {
  status: number;
  body: WorkspaceConversationCreatedDto | null;
} {
  const kind =
    typeof body === 'object' && body !== null && 'kind' in body
      ? String((body as { kind?: unknown }).kind ?? '')
      : '';
  if (kind !== 'SmartDiscovery') {
    return { status: 400, body: null };
  }
  const conversationId = crypto.randomUUID();
  const now = new Date().toISOString();
  upsertHeaderForNewConversation(conversationId);
  const created: WorkspaceConversationCreatedDto = {
    conversationId,
    kind: 'SmartDiscovery',
    createdAtUtc: now,
  };
  return { status: 201, body: created };
}

export function mockHandleWorkspaceConversationsDetailGet(canonical: string): {
  status: number;
  body: WorkspaceConversationDetailDto | null;
} {
  const match = /^\/api\/opportunities\/workspaces\/[^/]+\/conversations\/([^/]+)$/.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const conversationId = match[1];
  const header = findHeader(conversationId);
  if (!header) {
    return { status: 404, body: null };
  }
  const detail: WorkspaceConversationDetailDto = {
    conversationId: header.conversationId,
    kind: header.kind,
    opportunityId: header.opportunityId,
    title: header.title,
    messageCount: header.messageCount,
    createdAtUtc: header.createdAtUtc,
    updatedAtUtc: header.updatedAtUtc,
  };
  return { status: 200, body: detail };
}

export function mockHandleWorkspaceConversationsMessagesGet(canonical: string): {
  status: number;
  body: {
    items: WorkspaceConversationMessageDto[];
    nextBeforeSequence: null;
    hasMore: boolean;
  } | null;
} {
  const match = /^\/api\/opportunities\/workspaces\/[^/]+\/conversations\/([^/]+)\/messages$/.exec(
    canonical,
  );
  if (!match) {
    return { status: 404, body: null };
  }
  const conversationId = match[1];
  const items = mockMessagesByConversationId.get(conversationId) ?? [];
  return {
    status: 200,
    body: {
      items: items.map((row) => ({ ...row })),
      nextBeforeSequence: null,
      hasMore: false,
    },
  };
}

export function mockHandleSmartDiscoveryAnswerModelsGet(): {
  status: number;
  body: typeof SMART_DISCOVERY_ANSWER_MODEL_CATALOG;
} {
  return { status: 200, body: SMART_DISCOVERY_ANSWER_MODEL_CATALOG };
}

export function mockHandleWorkspaceConversationsChatPost(
  canonical: string,
  body: unknown,
): { status: number; body: typeof MOCK_SMART_DISCOVERY_CHAT_BODY } {
  const match = /^\/api\/opportunities\/workspaces\/[^/]+\/conversations\/([^/]+)\/chat$/.exec(
    canonical,
  );
  const conversationId = match?.[1] ?? '';
  const message =
    typeof body === 'object' && body !== null && 'message' in body
      ? String((body as { message?: unknown }).message ?? '').trim()
      : '';
  if (conversationId && message) {
    appendMockChatTurn(conversationId, message);
  }
  return { status: 200, body: MOCK_SMART_DISCOVERY_CHAT_BODY };
}
